import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { GoogleMap, LoadScript, Marker, Autocomplete } from '@react-google-maps/api';

const GOOGLE_API_KEY = 'AIzaSyAHQSuklfkVa-mATYvbA7odk3_LpnKG-OA';

// دالة لتقريب الإحداثيات إلى 7 أرقام عشرية
const roundCoordinate = (value) => {
    return Math.round(value * 10000000) / 10000000;
};

const libraries = ['places'];

const mapContainerStyle = {
    width: '100%',
    height: '100%'
};

const LocationPicker = ({ lat, lng, onLocationChange, height = '400px' }) => {
    const [position, setPosition] = useState(null);
    const [map, setMap] = useState(null);
    const [autocomplete, setAutocomplete] = useState(null);
    const [searchValue, setSearchValue] = useState('');

    // الموقع الافتراضي (القاهرة، مصر)
    const defaultCenter = useMemo(() => ({ 
        lat: 30.0444, 
        lng: 31.2357 
    }), []);

    // تحديث الموقع عند تغيير lat/lng من الخارج
    useEffect(() => {
        if (lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng))) {
            const newLat = parseFloat(lat);
            const newLng = parseFloat(lng);
            
            const newPos = {
                lat: newLat,
                lng: newLng
            };
            setPosition(newPos);
            
            // تحريك الخريطة للموقع الجديد
            if (map) {
                map.panTo(newPos);
                map.setZoom(18);
            }
        }
    }, [lat, lng, map]);

    // إرسال التغييرات للمكون الأب
    useEffect(() => {
        if (position && onLocationChange) {
            const timeoutId = setTimeout(() => {
                onLocationChange(roundCoordinate(position.lat), roundCoordinate(position.lng));
            }, 300);
            
            return () => clearTimeout(timeoutId);
        }
    }, [position, onLocationChange]);

    const center = useMemo(() => 
        position || defaultCenter, 
        [position, defaultCenter]
    );
    
    const handleMapClick = useCallback((e) => {
        const newPosition = {
            lat: roundCoordinate(e.latLng.lat()),
            lng: roundCoordinate(e.latLng.lng())
        };
        setPosition(newPosition);
    }, []);

    const handleLoad = useCallback((map) => {
        setMap(map);
    }, []);

    const handleAutocompleteLoad = useCallback((autocomplete) => {
        setAutocomplete(autocomplete);
    }, []);

    const handlePlaceChanged = useCallback(() => {
        if (autocomplete !== null) {
            const place = autocomplete.getPlace();
            if (place.geometry && place.geometry.location) {
                const newPosition = {
                    lat: roundCoordinate(place.geometry.location.lat()),
                    lng: roundCoordinate(place.geometry.location.lng())
                };
                setPosition(newPosition);
                
                if (map) {
                    map.panTo(newPosition);
                    map.setZoom(18);
                }
                
                setSearchValue(place.formatted_address || '');
            }
        }
    }, [autocomplete, map]);

    const mapOptions = useMemo(() => ({
        disableDefaultUI: false,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
        language: 'ar',
        region: 'EG',
    }), []);

    return (
        <div style={{ 
            border: '2px solid #e9ecef', 
            borderRadius: '12px', 
            overflow: 'visible',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 1
        }}>
            <LoadScript
                googleMapsApiKey={GOOGLE_API_KEY}
                libraries={libraries}
                language="ar"
                region="EG"
            >
                <div style={{ height, position: 'relative', overflow: 'visible' }}>
                    {/* Search Box */}
                    <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        zIndex: 10000,
                        width: '300px',
                        pointerEvents: 'auto',
                        backgroundColor: 'transparent'
                    }}>
                        <Autocomplete
                            onLoad={handleAutocompleteLoad}
                            onPlaceChanged={handlePlaceChanged}
                            options={{
                                componentRestrictions: { country: [] },
                                fields: ['formatted_address', 'geometry', 'name'],
                            }}
                        >
                            <input
                                type="text"
                                placeholder="ابحث عن أي مكان في العالم..."
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: '14px',
                                    borderRadius: '8px',
                                    border: '2px solid #e9ecef',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    direction: 'rtl',
                                    textAlign: 'right',
                                    background: 'white',
                                    outline: 'none',
                                    zIndex: 10000,
                                    position: 'relative',
                                    display: 'block',
                                    visibility: 'visible',
                                    opacity: 1
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#edc80e';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(237, 200, 14, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e9ecef';
                                    e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                }}
                            />
                        </Autocomplete>
                    </div>

                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={center}
                        zoom={position ? 18 : 11}
                        onClick={handleMapClick}
                        onLoad={handleLoad}
                        options={mapOptions}
                    >
                        {position && (
                            <Marker
                                position={position}
                                draggable={true}
                                onDragEnd={(e) => {
                                    const newPosition = {
                                        lat: roundCoordinate(e.latLng.lat()),
                                        lng: roundCoordinate(e.latLng.lng())
                                    };
                                    setPosition(newPosition);
                                }}
                            />
                        )}
                    </GoogleMap>
                    </div>
                </div>
            </LoadScript>
            
            {/* Display coordinates */}
            {position && (
                <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    direction: 'rtl'
                }}>
                    📍 {position.lat.toFixed(7)}, {position.lng.toFixed(7)}
                </div>
            )}
        </div>
    );
};

LocationPicker.displayName = 'LocationPicker';

// إضافة CSS لضمان ظهور dropdown البحث بشكل صحيح
if (typeof document !== 'undefined') {
    const styleId = 'location-picker-autocomplete-styles';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
            .pac-container {
                z-index: 10001 !important;
                border-radius: 8px !important;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
                margin-top: 4px !important;
                font-family: inherit !important;
            }
            .pac-item {
                padding: 10px 16px !important;
                cursor: pointer !important;
                border-bottom: 1px solid #e9ecef !important;
            }
            .pac-item:hover {
                background-color: #f8f9fa !important;
            }
            .pac-item-selected {
                background-color: #e9ecef !important;
            }
            .pac-icon {
                margin-right: 8px !important;
            }
        `;
        document.head.appendChild(style);
    }
}

export default React.memo(LocationPicker);

